import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import { getSupabaseClient } from "../../providers/supabase/supabase";
import { useOffers } from "../useOffers";

/**
 * Termómetro de éxito (PRD Bloque E). El cálculo vive en la BD
 * (thermo_factors, deal_thermo, deal_board); aquí solo se muestra.
 */

// Niveles del prototipo (THL)
export const TH_LEVELS = [
  [80, "Muy caliente", "--th-4"],
  [60, "Caliente", "--th-3"],
  [40, "Tibio", "--th-2"],
  [0, "Frío", "--th-1"],
] as const;
export const thLevel = (v: number) => TH_LEVELS.find((l) => v >= l[0])!;

/** Barra del termómetro: pista, punto de color, número y nivel. */
export const ThermoBar = ({
  value,
  mini,
  className,
}: {
  value: number | null | undefined;
  mini?: boolean;
  className?: string;
}) => {
  if (value == null) return <span className="text-muted-foreground">—</span>;
  const [, label, color] = thLevel(value);
  return (
    <span
      className={cn("inline-flex items-center gap-1.5", className)}
      title={`Termómetro ${value}/100 · ${label}`}
    >
      <span className="h-1.5 w-12 overflow-hidden rounded-full bg-secondary">
        <i
          className="block h-full rounded-full"
          style={{ width: `${value}%`, background: `var(${color})` }}
        />
      </span>
      <b className="tabular-nums">{value}</b>
      {!mini && <span className="text-xs text-muted-foreground">{label}</span>}
    </span>
  );
};

// Prioridad tipo Jira: termómetro + 10 (producto prioridad 1), 0 (2), −10 (3)
const PRI = [
  [80, "Máxima", "#CD1317", "M4 9l4-4 4 4M4 13l4-4 4 4"],
  [65, "Alta", "#E9494A", "M4 11l4-4 4 4"],
  [45, "Media", "#E97F33", "M3 6.5h10M3 10.5h10"],
  [30, "Baja", "#2D8738", "M4 6l4 4 4-4"],
  [0, "Mínima", "#57A55A", "M4 4l4 4 4-4M4 8l4 4 4-4"],
] as const;
export const priScore = (thermo: number, offerPriority?: number | null) =>
  thermo + (offerPriority === 1 ? 10 : offerPriority === 3 ? -10 : 0);
export const priOf = (thermo: number, offerPriority?: number | null) =>
  PRI.find((p) => priScore(thermo, offerPriority) >= p[0]) ?? PRI[4];

export const PriorityIcon = ({
  thermo,
  offerPriority,
  withLabel,
}: {
  thermo: number | null | undefined;
  offerPriority?: number | null;
  withLabel?: boolean;
}) => {
  if (thermo == null) return null;
  const [, label, color, d] = priOf(thermo, offerPriority);
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-semibold"
      title={`Prioridad ${label}`}
    >
      <svg
        viewBox="0 0 16 16"
        className="size-4 shrink-0"
        fill="none"
        stroke={color}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d={d} />
      </svg>
      {withLabel ? label : <span className="sr-only">Prioridad {label}</span>}
    </span>
  );
};

/** Tendencia de 8 semanas (7 fotos semanales + hoy) y cambio en 2 semanas. */
export const Trend = ({
  weeks,
  value,
  change,
  big,
}: {
  weeks: (number | null)[] | null | undefined;
  value: number | null | undefined;
  change: number | null | undefined;
  big?: boolean;
}) => {
  if (change == null || value == null)
    return (
      <span className="text-xs text-muted-foreground">
        {big ? "Tendencia disponible en 2 semanas" : "tendencia en 2 sem."}
      </span>
    );
  const s = [...(weeks ?? []).filter((x): x is number => x != null), value];
  const w = big ? 180 : 64;
  const h = big ? 40 : 20;
  const mn = Math.min(...s);
  const mx = Math.max(...s);
  const pts = s
    .map(
      (v, i) =>
        `${((i / Math.max(1, s.length - 1)) * w).toFixed(1)},${(h - 2 - ((v - mn) / (mx - mn || 1)) * (h - 4)).toFixed(1)}`,
    )
    .join(" ");
  const col =
    change < 0 ? "var(--critical)" : change > 0 ? "var(--good)" : "gray";
  return (
    <span
      className="inline-flex items-center gap-1 text-xs"
      title={`Cambio en 2 semanas: ${change > 0 ? "+" : ""}${change}`}
    >
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden>
        <polyline
          points={pts}
          fill="none"
          stroke={col}
          strokeWidth={1.8}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      <b style={{ color: col }}>
        {change > 0 ? "▲" : change < 0 ? "▼" : "■"}
        {Math.abs(change)}
      </b>
      {big && <span className="text-muted-foreground">en 2 semanas</span>}
    </span>
  );
};

// Desglose: {"fit":[puntos, máximo], …} (deal_thermo / directory_thermo)
export type ThermoParts = Record<
  "fit" | "cont" | "size" | "sig" | "int",
  [number, number]
>;
export const TH_LAB: Record<keyof ThermoParts, string> = {
  fit: "Encaje con la oferta",
  cont: "Contactabilidad",
  size: "Tamaño y poder de compra",
  sig: "Señales (cercanía, web, crecimiento, IA, reseñas)",
  int: "Interacción contigo",
};

/** "Sube por / Le falta" (why() del prototipo). */
export const why = (parts: ThermoParts, decay = 0) => {
  const P = (Object.keys(TH_LAB) as (keyof ThermoParts)[])
    .filter((k) => parts[k]?.[1] > 0)
    .map(
      (k) =>
        [
          TH_LAB[k].split(" (")[0].toLowerCase(),
          parts[k][0] / parts[k][1],
        ] as const,
    );
  const hi = [...P]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map((p) => p[0]);
  const lo = [...P].sort((a, b) => a[1] - b[1])[0]?.[0] ?? "—";
  return {
    up: hi.join(" y "),
    low: lo + (decay ? ` · pierde ${decay} pts por inactividad` : ""),
  };
};

/** Desglose de los 5 factores y el enfriamiento. */
export const ThermoBreakdown = ({
  parts,
  decay,
  idle,
}: {
  parts: ThermoParts;
  decay?: number | null;
  idle?: number | null;
}) => (
  <ul className="flex flex-col gap-1.5 text-sm">
    {(Object.keys(TH_LAB) as (keyof ThermoParts)[]).map((k) => {
      const [pts, max] = parts[k] ?? [0, 0];
      return (
        <li key={k} className="grid grid-cols-[1fr_auto] items-center gap-x-2">
          <span className="truncate" title={TH_LAB[k]}>
            {TH_LAB[k].split(" (")[0]}
          </span>
          <b className="tabular-nums">
            {pts}
            <span className="font-normal text-muted-foreground">/{max}</span>
          </b>
          <span className="col-span-2 h-1 overflow-hidden rounded-full bg-secondary">
            <i
              className="block h-full rounded-full bg-primary"
              style={{ width: `${max ? (pts / max) * 100 : 0}%` }}
            />
          </span>
        </li>
      );
    })}
    {!!decay && (
      <li className="flex justify-between font-semibold text-[var(--critical)]">
        <span>Enfriamiento: {idle} días sin contacto</span>
        <span>−{decay}</span>
      </li>
    )}
  </ul>
);

/** Termómetro y prioridad de cada oportunidad, para listas (Tareas, ficha). */
export const useDealThermos = () =>
  useQuery({
    queryKey: ["deal_board", "thermos"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("deal_board")
        .select("id, thermo, offer_priority");
      if (error) throw error;
      return new Map(
        (data ?? []).map((r) => [
          r.id as number,
          r as { thermo: number | null; offer_priority: number | null },
        ]),
      );
    },
  });

export const DealThermo = ({ dealId }: { dealId?: number | null }) => {
  const { data } = useDealThermos();
  const t = dealId ? data?.get(dealId) : undefined;
  if (t?.thermo == null) return null;
  return (
    <span className="inline-flex items-center gap-2">
      <PriorityIcon thermo={t.thermo} offerPriority={t.offer_priority} />
      <ThermoBar value={t.thermo} mini />
    </span>
  );
};

// ---------------------------------------------------------------------
// Alertas (función thermo_alerts): campana y Mi día
// ---------------------------------------------------------------------
export type ThermoAlert = {
  kind: "idle" | "down" | "up" | "hot" | "quejas" | "cerrada" | "zombie";
  deal_id: number | null;
  deal_key: string | null;
  company_id: number | null;
  directory_id: number | null;
  name: string;
  thermo: number;
  idle_days: number | null;
  change: number | null;
  fits: string[] | null;
  estado: string | null;
};

export const useThermoAlerts = () =>
  useQuery({
    queryKey: ["thermo_alerts"],
    staleTime: 60_000,
    queryFn: async () => {
      const sb = getSupabaseClient();
      const [a, q, c, z] = await Promise.all([
        sb.rpc("thermo_alerts"),
        sb.rpc("review_alerts"), // quejas en aumento en sus reseñas (Bloque H)
        sb.rpc("denue_alerts"), // cerrada según el DENUE con oportunidades abiertas (Bloque J)
        sb.rpc("zombie_alerts"), // sin compromiso ni siguiente paso (Bloque T.5)
      ]);
      if (a.error) throw a.error;
      const zombieDeals = (z.data ?? []) as {
        deal_id: number;
        key: string;
        company_name: string;
        touches: number;
      }[];
      // zombie_alerts() no trae company_id (solo el nombre): se completa con
      // una consulta a deals, ya legible por cualquier autenticado.
      const { data: zd } = zombieDeals.length
        ? await sb
            .from("deals")
            .select("id, company_id")
            .in(
              "id",
              zombieDeals.map((x) => x.deal_id),
            )
        : { data: [] };
      const zombies: ThermoAlert[] = zombieDeals.map((x) => ({
        kind: "zombie",
        deal_id: x.deal_id,
        deal_key: x.key,
        company_id: zd?.find((d) => d.id === x.deal_id)?.company_id ?? null,
        directory_id: null,
        name: x.company_name,
        thermo: 0,
        idle_days: null,
        change: null,
        fits: null,
        estado: `${x.touches} toques sin compromiso`,
      }));
      const all = [
        ...zombies,
        ...((c.data ?? []) as ThermoAlert[]),
        ...((q.data ?? []) as ThermoAlert[]),
        ...(a.data ?? []),
      ];
      return all as ThermoAlert[];
    },
  });

const ALC: Record<ThermoAlert["kind"], [string, string]> = {
  idle: ["var(--critical)", "!"],
  down: ["var(--critical)", "▼"],
  up: ["var(--good)", "▲"],
  hot: ["var(--th-3)", "★"],
  quejas: ["var(--critical)", "▲"],
  cerrada: ["var(--critical)", "×"],
  zombie: ["var(--critical)", "Z"],
};

const alertText = (
  a: ThermoAlert,
  offerName: (slug: string) => string,
): [string, string, string] => {
  const to =
    a.kind === "hot"
      ? `/directory/${a.directory_id}/show`
      : `/companies/${a.company_id}/show`;
  switch (a.kind) {
    case "idle":
      return [
        `${a.name} se está enfriando`,
        `${a.deal_key} · ${a.idle_days} días sin contacto · termómetro ${a.thermo}`,
        to,
      ];
    case "down":
      return [
        `${a.name} bajó ${-(a.change ?? 0)} puntos`,
        `${a.deal_key} · en 2 semanas · termómetro ${a.thermo}`,
        to,
      ];
    case "up":
      return [
        `${a.name} se calentó (+${a.change})`,
        `${a.deal_key} · buen momento para avanzar`,
        to,
      ];
    case "cerrada":
      return [
        `${a.name} aparece cerrada en el DENUE`,
        `${a.deal_key} sigue abierta · corte del ${a.estado} · confirma con una llamada antes de moverla`,
        to,
      ];
    case "quejas":
      return [
        `${a.name}: quejas en aumento en Google`,
        `${a.deal_key} · «${a.estado}» +${a.change} pts en 90 días · buen momento para llamar`,
        to,
      ];
    case "zombie":
      return [
        `${a.name}: prospecto zombi`,
        `${a.deal_key} · ${a.estado} · sin compromiso ni siguiente paso, dale un empujón o ciérralo`,
        to,
      ];
    default:
      return [
        `${a.name} está caliente y no está en el CRM`,
        `Termómetro ${a.thermo} · ${a.estado} · encaja en ${(a.fits ?? []).map(offerName).join(", ")}`,
        to,
      ];
  }
};

export const AlertList = ({
  alerts,
  onPick,
}: {
  alerts: ThermoAlert[];
  onPick?: () => void;
}) => {
  const { data: offers } = useOffers();
  const offerName = (slug: string) =>
    offers?.find((o) => o.slug === slug)?.name ?? slug;
  return (
    <ul className="flex flex-col gap-1">
      {alerts.map((a, i) => {
        const [t, s, to] = alertText(a, offerName);
        const [color, icon] = ALC[a.kind];
        return (
          <li key={i}>
            <Link
              to={to}
              onClick={onPick}
              className="flex items-start gap-2.5 rounded-lg p-2 hover:bg-secondary"
            >
              <span
                className="grid size-6 shrink-0 place-items-center rounded-full text-xs font-extrabold text-white"
                style={{ background: color }}
                aria-hidden
              >
                {icon}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold">{t}</span>
                <span className="block text-xs text-muted-foreground">{s}</span>
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
};

// ---------------------------------------------------------------------
// Pesos (Ajustes): mover uno reparte el resto para que siempre sumen 100
// ---------------------------------------------------------------------
export type Weights = Record<keyof ThermoParts, number>;
export const W_KEYS = Object.keys(TH_LAB) as (keyof ThermoParts)[];
export const rebalance = (
  w: Weights,
  k: keyof ThermoParts,
  x: number,
): Weights => {
  const others = W_KEYS.filter((o) => o !== k);
  const rest = 100 - x;
  const sum = others.reduce((s, o) => s + w[o], 0);
  const next = { ...w, [k]: x };
  others.forEach(
    (o) =>
      (next[o] = Math.round(sum ? (w[o] * rest) / sum : rest / others.length)),
  );
  // el redondeo puede dejar ±1-2: se ajusta en el mayor de los otros
  const diff = 100 - W_KEYS.reduce((s, o) => s + next[o], 0);
  const big = [...others].sort((a, b) => next[b] - next[a])[0];
  next[big] += diff;
  return next;
};
